import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import {
  tournamentApi,
  type ActiveTournamentResponse,
  type PendingForAdminRow,
  type PlayerEntry,
  type RosterRow,
} from '@/api/tournament';
import { CasinoButton } from '@/components/CasinoButton';
import { Flaming7 } from '@/components/Flaming7';
import { LedNumber } from '@/components/LedNumber';
import { PhotoModal } from '@/components/PhotoModal';
import { SignPanel } from '@/components/SignPanel';
import { useSession } from '@/state/session';
import { formatDollars } from '@/lib/payouts';
import { cn } from '@/lib/utils';

type EntryState =
  | 'not_entered'
  | 'entered_unscored'
  | 'score_submitted_pending'
  | 'verified';

function classify(entries: PlayerEntry[]): EntryState {
  if (entries.length === 0) return 'not_entered';
  // Show the most urgent state across all entries.
  if (entries.some((e) => e.verifiedByAdmin)) {
    // If ANY are verified but others still need work, show the not-yet-verified state first
    if (entries.some((e) => !e.submittedAt)) return 'entered_unscored';
    if (entries.some((e) => e.submittedAt && !e.verifiedByAdmin))
      return 'score_submitted_pending';
    return 'verified';
  }
  if (entries.some((e) => !e.submittedAt)) return 'entered_unscored';
  return 'score_submitted_pending';
}

export function TournamentActive() {
  const q = useQuery({
    queryKey: ['tournament', 'active'],
    queryFn: tournamentApi.active,
    refetchOnMount: 'always',
  });

  if (q.isLoading) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }
  if (!q.data) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-flame-red">Couldn't load tournament data.</p>
      </main>
    );
  }
  return <Inner data={q.data} />;
}

function Inner({ data }: { data: ActiveTournamentResponse }) {
  const {
    tournament,
    potCents,
    totalEntries,
    submittedCount,
    verifiedCount,
    roster,
    myEntries,
    canJoin,
    isApproved,
    pendingForAdmin,
  } = data;
  const user = useSession((s) => s.user);
  const isElevatedAdmin = !!(user?.isAdmin && user.sessionElevated);

  return (
    <main className="mx-auto max-w-md px-5 py-6 space-y-5">
      {!tournament ? (
        <NoActive />
      ) : (
        <>
          <TournamentCard
            t={tournament}
            potCents={potCents}
            totalEntries={totalEntries}
            submittedCount={submittedCount}
            verifiedCount={verifiedCount}
          />

          {!isApproved && <PendingApprovalPanel />}

          {isApproved && (
            <PlayerStateCard
              entries={myEntries}
              canJoin={canJoin}
              submittedCount={submittedCount}
              verifiedCount={verifiedCount}
              totalEntries={totalEntries}
            />
          )}

          {isElevatedAdmin &&
            pendingForAdmin &&
            pendingForAdmin.length > 0 && (
              <PendingVerifyCard
                pending={pendingForAdmin}
                tournamentId={tournament.id}
              />
            )}

          {myEntries.length > 0 && <EntryList entries={myEntries} />}

          {roster.length > 0 && <RosterList roster={roster} />}

          <Link
            to="/tournament/active/leaderboard"
            className="block rounded-xl border-2 border-cabinet-gold bg-cabinet-purple-deep px-4 py-3 text-center font-display uppercase tracking-widest text-cabinet-gold hover:bg-cabinet-purple"
          >
            View leaderboard →
          </Link>
        </>
      )}
    </main>
  );
}

function TournamentCard({
  t,
  potCents,
  totalEntries,
  submittedCount,
  verifiedCount,
}: {
  t: NonNullable<ActiveTournamentResponse['tournament']>;
  potCents: number;
  totalEntries: number;
  submittedCount: number;
  verifiedCount: number;
}) {
  const awaitingVerify = Math.max(0, submittedCount - verifiedCount);

  return (
    <section className="cabinet-card overflow-hidden">
      <div className="bg-gradient-to-b from-cabinet-purple to-cabinet-purple-deep relative px-5 pt-5 pb-4">
        <div className="absolute top-6 -right-6 opacity-90 pointer-events-none">
          <Flaming7 size={100} />
        </div>
        <p className="font-display uppercase tracking-widest text-cabinet-gold-bright text-xs">
          {t.machineName}
        </p>
        <SignPanel size="md" className="mt-1 max-w-[80%]">
          <h2 className="text-marquee text-3xl uppercase leading-none">
            {t.name}
          </h2>
        </SignPanel>
      </div>

      <div className="grid grid-cols-2 gap-3 px-5 py-5 border-b border-cabinet-gold/20">
        <Stat label="Pot">
          <LedNumber value={Math.round(potCents / 100)} currency digits={5} size="md" />
        </Stat>
        <Stat label="Entries">
          <LedNumber value={totalEntries} digits={3} size="md" />
        </Stat>
      </div>

      {/* Aggregate progress — names + scores intentionally NOT exposed */}
      <p className="px-5 py-3 text-[11px] uppercase tracking-widest text-cabinet-cream/60 text-center border-b border-cabinet-gold/20">
        {totalEntries === 0 ? (
          <span>Be the first to join</span>
        ) : (
          <>
            <span className="text-cabinet-cream">{submittedCount}</span> submitted ·{' '}
            <span className="text-cabinet-gold-bright">{verifiedCount}</span> verified
            {awaitingVerify > 0 && (
              <>
                {' · '}
                <span className="text-cabinet-flame-orange">
                  {awaitingVerify} awaiting
                </span>
              </>
            )}
          </>
        )}
      </p>

      <div className="px-5 py-4">
        <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60 mb-2">
          Payouts
        </p>
        <ul className="grid grid-cols-3 gap-2">
          {t.payoutStructure.map((p) => {
            const amount = Math.round((potCents * p.pct) / 100);
            return (
              <li
                key={p.place}
                className="rounded-md bg-cabinet-cream/5 px-3 py-2 ring-1 ring-cabinet-gold/30"
              >
                <p className="font-display text-cabinet-gold text-lg leading-none">
                  {placeLabel(p.place)}
                </p>
                <p className="font-display text-cabinet-cream text-base mt-0.5">
                  {formatDollars(amount)}
                </p>
                <p className="text-[10px] text-cabinet-cream/50">{p.pct}%</p>
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-cabinet-cream/50 mt-3">
          Entry {formatDollars(t.entryFeeCents)} · {t.pullCount} pulls × {t.creditsPerPull}
          · max {t.maxEntriesPerPlayer} entries / player
        </p>
      </div>
    </section>
  );
}

function NoActive() {
  return (
    <section className="cabinet-card p-6 text-center space-y-3">
      <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
        No tournament running
      </h2>
      <p className="text-sm text-cabinet-cream/70">
        Check back when the next one starts.
      </p>
      <Link
        to="/me"
        className="inline-block text-cabinet-gold underline-offset-2 hover:underline text-sm"
      >
        ← Back to my account
      </Link>
    </section>
  );
}

function PendingApprovalPanel() {
  return (
    <div className="rounded-xl border-2 border-cabinet-gold bg-cabinet-purple-deep px-4 py-4 text-sm leading-snug">
      <p className="font-display uppercase tracking-widest text-cabinet-gold text-xs mb-2">
        Pending approval
      </p>
      <p className="text-cabinet-cream/80">
        Your account is waiting for admin approval before you can join. You can still see
        the tournament details and the leaderboard once it starts.
      </p>
    </div>
  );
}

function PlayerStateCard({
  entries,
  canJoin,
  submittedCount,
  verifiedCount,
  totalEntries,
}: {
  entries: PlayerEntry[];
  canJoin: boolean;
  submittedCount: number;
  verifiedCount: number;
  totalEntries: number;
}) {
  const state = classify(entries);
  const inProgressEntry = entries.find((e) => !e.submittedAt);
  const pendingVerify = entries.find((e) => e.submittedAt && !e.verifiedByAdmin);

  switch (state) {
    case 'not_entered':
      return (
        <section className="cabinet-card p-5 text-center space-y-3">
          <p className="text-sm text-cabinet-cream/70">
            Ready to play? Tap below to lock in your entry.
          </p>
          <Link to="/tournament/active/join">
            <CasinoButton fullWidth disabled={!canJoin}>
              {canJoin ? 'Join tournament' : 'Joined max entries'}
            </CasinoButton>
          </Link>
        </section>
      );
    case 'entered_unscored':
      return (
        <section className="cabinet-card p-5 space-y-3">
          <p className="text-sm text-cabinet-cream">
            You're in — submit your scores when you're done playing.
          </p>
          {inProgressEntry && (
            <Link to={`/tournament/active/submit/${inProgressEntry.id}`}>
              <CasinoButton fullWidth>
                Submit entry #{inProgressEntry.entryNumber}
              </CasinoButton>
            </Link>
          )}
          {canJoin && (
            <Link
              to="/tournament/active/join"
              className="block text-sm text-cabinet-gold underline-offset-2 hover:underline text-center"
            >
              + Add another entry
            </Link>
          )}
        </section>
      );
    case 'score_submitted_pending':
      return (
        <section className="cabinet-card p-5 text-center space-y-3">
          <p className="font-display uppercase tracking-widest text-cabinet-gold-bright text-xs">
            Submitted ✓
          </p>
          <p className="text-sm text-cabinet-cream/80">
            Awaiting admin verification
            {pendingVerify ? ` for entry #${pendingVerify.entryNumber}` : ''}. You'll
            see the leaderboard once your score is verified.
          </p>
          {totalEntries > 0 && (
            <p className="text-xs text-cabinet-cream/60">
              <span className="font-display text-cabinet-gold-bright">
                {verifiedCount}
              </span>{' '}
              of <span className="font-display">{submittedCount}</span> submitted
              entries verified so far.
            </p>
          )}
          {canJoin && (
            <Link to="/tournament/active/join">
              <CasinoButton variant="ghost">+ Add another entry</CasinoButton>
            </Link>
          )}
        </section>
      );
    case 'verified':
      return (
        <section className="cabinet-card p-5 text-center space-y-3">
          <p className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
            Verified ✓
          </p>
          <p className="text-sm text-cabinet-cream/80">
            Your score is locked in. Leaderboard coming soon.
          </p>
        </section>
      );
  }
}

function EntryList({ entries }: { entries: PlayerEntry[] }) {
  const [photoEntry, setPhotoEntry] = useState<PlayerEntry | null>(null);
  const showEntryNumbers = entries.length > 1;

  return (
    <section className="cabinet-card p-5 space-y-3">
      <h3 className="font-display text-cabinet-gold text-sm uppercase tracking-widest">
        Your entries
      </h3>
      <ul className="space-y-2">
        {entries.map((e) => {
          const hasPhotos = e.hasStartingPhoto || e.hasEndingPhoto;
          const Wrapper: React.ElementType = hasPhotos ? 'button' : 'div';
          return (
            <li key={e.id}>
              <Wrapper
                type={hasPhotos ? 'button' : undefined}
                onClick={hasPhotos ? () => setPhotoEntry(e) : undefined}
                className={cn(
                  'w-full flex items-center gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2 text-left',
                  hasPhotos && 'hover:ring-cabinet-gold/40 transition cursor-pointer min-h-[44px]',
                )}
              >
                <div className="flex-1 min-w-0">
                  {showEntryNumbers && (
                    <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/40">
                      Entry #{e.entryNumber}
                    </p>
                  )}
                  <p className="text-xs text-cabinet-cream/70 tabular-nums">
                    {e.startingBalance !== null && e.endingBalance !== null
                      ? `${e.startingBalance} → ${e.endingBalance}`
                      : 'pending submission'}
                    {hasPhotos && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-cream/50">
                        · tap for photos
                      </span>
                    )}
                  </p>
                  <div className="mt-1 flex gap-2 text-[10px] uppercase tracking-widest">
                    <span
                      className={badge(e.paid ? 'good' : 'warn')}
                      title={
                        e.paid
                          ? 'Entry fee paid'
                          : "Entry fee not yet paid to the organizer"
                      }
                    >
                      {e.paid ? 'Paid' : 'Unpaid'}
                    </span>
                    <span
                      className={badge(
                        e.verifiedByAdmin ? 'good' : e.submittedAt ? 'pending' : 'idle',
                      )}
                      title={
                        e.verifiedByAdmin
                          ? 'Score confirmed by admin'
                          : e.submittedAt
                            ? 'Score submitted, awaiting admin verification'
                            : 'You have not submitted a score for this entry yet'
                      }
                    >
                      {e.verifiedByAdmin
                        ? 'Verified'
                        : e.submittedAt
                          ? 'Pending verify'
                          : 'Not submitted'}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
                    Score
                  </p>
                  <p className="font-led tabular-nums text-cabinet-led-on text-lg">
                    {e.score ?? '—'}
                  </p>
                </div>
              </Wrapper>
            </li>
          );
        })}
      </ul>
      {photoEntry && (
        <PhotoModal
          entryId={photoEntry.id}
          title={
            showEntryNumbers ? `Entry #${photoEntry.entryNumber}` : 'Your entry'
          }
          startingBalance={photoEntry.startingBalance}
          endingBalance={photoEntry.endingBalance}
          hasStartingPhoto={photoEntry.hasStartingPhoto}
          hasEndingPhoto={photoEntry.hasEndingPhoto}
          onClose={() => setPhotoEntry(null)}
        />
      )}
    </section>
  );
}

const ROSTER_LEGEND_DISMISS_KEY = 'slot-tournament:roster-legend-dismissed';

function RosterList({ roster }: { roster: RosterRow[] }) {
  // Show the legend until the player dismisses it once — localStorage keeps
  // the choice across sessions on the same device.
  const [showLegend, setShowLegend] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(ROSTER_LEGEND_DISMISS_KEY) !== '1';
  });

  function dismissLegend() {
    try {
      window.localStorage.setItem(ROSTER_LEGEND_DISMISS_KEY, '1');
    } catch {
      /* private browsing, etc. — fine to ignore */
    }
    setShowLegend(false);
  }

  return (
    <section className="cabinet-card p-5 space-y-3">
      <h3 className="font-display text-cabinet-gold text-sm uppercase tracking-widest">
        Players ({roster.length})
      </h3>
      <ul className="space-y-2">
        {roster.map((r) => {
          // Tooltip copy adapts to whether the player has multiple entries
          // (where "all paid" / "all verified" semantics matter).
          const paidTitle = r.allPaid
            ? r.entryCount > 1
              ? 'All entry fees paid'
              : 'Entry fee paid'
            : r.entryCount > 1
              ? 'At least one entry fee not yet paid'
              : 'Entry fee not yet paid';
          const stateTitle = r.allVerified
            ? r.entryCount > 1
              ? 'All scores confirmed by admin'
              : 'Score confirmed by admin'
            : r.anySubmitted
              ? 'Score submitted, awaiting admin verification'
              : 'Entered, no score submitted yet';
          return (
            <li
              key={r.userId}
              className="flex items-center gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-cabinet-cream truncate">
                  {r.displayName}
                  {r.entryCount > 1 && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-cream/40">
                      {r.entryCount} entries
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-1.5 text-[10px] uppercase tracking-widest">
                <span
                  className={badge(r.allPaid ? 'good' : 'warn')}
                  title={paidTitle}
                  aria-label={paidTitle}
                >
                  {r.allPaid ? 'Paid' : 'Unpaid'}
                </span>
                <span
                  className={badge(
                    r.allVerified ? 'good' : r.anySubmitted ? 'pending' : 'idle',
                  )}
                  title={stateTitle}
                  aria-label={stateTitle}
                >
                  {r.allVerified
                    ? 'Verified'
                    : r.anySubmitted
                      ? 'Pending'
                      : 'Playing'}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {showLegend && (
        <div className="relative rounded-md bg-cabinet-purple-ink/60 ring-1 ring-cabinet-cream/10 px-3 py-2 pr-8 text-[11px] leading-relaxed text-cabinet-cream/70">
          <p>
            <strong className="text-cabinet-cream/90">Playing</strong> entered,
            no score yet ·{' '}
            <strong className="text-cabinet-cream/90">Pending</strong> awaiting
            admin verification ·{' '}
            <strong className="text-cabinet-gold-bright">Verified</strong> score
            confirmed
          </p>
          <button
            type="button"
            onClick={dismissLegend}
            aria-label="Dismiss legend"
            className="absolute top-1 right-1 h-6 w-6 rounded text-cabinet-cream/40 hover:text-cabinet-cream text-base leading-none"
          >
            ×
          </button>
        </div>
      )}
    </section>
  );
}

function badge(kind: 'good' | 'warn' | 'pending' | 'idle') {
  return cn(
    'rounded-full px-2 py-0.5 ring-1',
    kind === 'good' && 'bg-cabinet-gold/20 text-cabinet-gold-bright ring-cabinet-gold/40',
    kind === 'warn' && 'bg-cabinet-flame-orange/20 text-cabinet-flame-orange ring-cabinet-flame-orange/40',
    kind === 'pending' && 'bg-cabinet-flame-orange/20 text-cabinet-flame-orange ring-cabinet-flame-orange/40',
    kind === 'idle' && 'bg-cabinet-cream/10 text-cabinet-cream/60 ring-cabinet-cream/20',
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function placeLabel(p: number): string {
  const v = p % 100;
  if (v >= 11 && v <= 13) return `${p}th`;
  switch (p % 10) {
    case 1:
      return `${p}st`;
    case 2:
      return `${p}nd`;
    case 3:
      return `${p}rd`;
    default:
      return `${p}th`;
  }
}

/**
 * Admin one-tap verification queue. Each row is a pending entry the admin
 * can verify, peek photos for, or jump into the manage view to edit. The
 * row vanishes from the card when verified (the SSE event + refetch handle
 * the update — we don't need optimistic updates here).
 */
function PendingVerifyCard({
  pending,
  tournamentId,
}: {
  pending: PendingForAdminRow[];
  tournamentId: number;
}) {
  const qc = useQueryClient();
  const [photoEntry, setPhotoEntry] = useState<PendingForAdminRow | null>(null);
  // Track which row is currently being verified so we can disable the button.
  const [busyId, setBusyId] = useState<number | null>(null);

  const verify = useMutation({
    mutationFn: (entryId: number) => adminApi.toggleEntryVerified(entryId),
    onMutate: (entryId) => setBusyId(entryId),
    onSettled: () => setBusyId(null),
    onSuccess: async () => {
      // SSE will trigger this too, but invalidate immediately so the row
      // disappears even on a flaky network.
      await qc.invalidateQueries({ queryKey: ['tournament', 'active'] });
      await qc.invalidateQueries({ queryKey: ['leaderboard', tournamentId] });
    },
  });

  return (
    <section className="cabinet-card p-5 space-y-3 border-cabinet-flame-orange/40">
      <header className="flex items-center justify-between">
        <h3 className="font-display text-cabinet-flame-orange text-base sm:text-lg uppercase tracking-widest">
          Pending verification ({pending.length})
        </h3>
        <span className="text-[10px] uppercase tracking-widest text-cabinet-cream/40">
          Admin only
        </span>
      </header>

      <ul className="space-y-2">
        {pending.map((p) => (
          <li
            key={p.id}
            className="rounded-md ring-1 ring-cabinet-flame-orange/30 bg-cabinet-purple-ink/50 px-3 py-3 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-cabinet-cream truncate">
                {p.userDisplayName}
                {p.entryCountForUser > 1 && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-cream/40">
                    Entry #{p.entryNumber}
                  </span>
                )}
                {!p.paid && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-flame-orange">
                    Unpaid
                  </span>
                )}
              </p>
              <p className="text-xs text-cabinet-cream/60 tabular-nums">
                {p.startingBalance !== null && p.endingBalance !== null
                  ? `${p.startingBalance} → ${p.endingBalance}`
                  : 'no balances'}
                <span className="ml-2 text-cabinet-cream/40">·</span>
                <span className="ml-2 uppercase tracking-widest text-cabinet-cream/50 text-[10px]">
                  Score
                </span>{' '}
                <span className="font-led text-cabinet-led-on text-sm">
                  {p.score ?? '—'}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {(p.hasStartingPhoto || p.hasEndingPhoto) && (
                <button
                  type="button"
                  onClick={() => setPhotoEntry(p)}
                  className="text-xs text-cabinet-gold hover:text-cabinet-gold-bright min-h-[44px] px-2"
                >
                  View photos
                </button>
              )}
              <Link
                to={`/admin/tournament/${tournamentId}/manage`}
                className="text-xs text-cabinet-cream/60 hover:text-cabinet-cream min-h-[44px] px-2 inline-flex items-center"
              >
                Edit
              </Link>
              <CasinoButton
                variant="red"
                onClick={() => verify.mutate(p.id)}
                disabled={busyId === p.id}
                fullWidth={false}
              >
                {busyId === p.id ? 'Verifying…' : 'Verify'}
              </CasinoButton>
            </div>
          </li>
        ))}
      </ul>

      {verify.isError && (
        <p className="text-xs text-cabinet-flame-orange">
          Couldn't verify. Try the manage view.
        </p>
      )}

      {photoEntry && (
        <PhotoModal
          entryId={photoEntry.id}
          title={photoEntry.userDisplayName}
          startingBalance={photoEntry.startingBalance}
          endingBalance={photoEntry.endingBalance}
          hasStartingPhoto={photoEntry.hasStartingPhoto}
          hasEndingPhoto={photoEntry.hasEndingPhoto}
          onClose={() => setPhotoEntry(null)}
        />
      )}
    </section>
  );
}
